import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  TrendingUp, Plus, Upload, Paperclip, X, Send, 
  Building2, FileText, Calendar, User, MessageSquare,
  CheckCircle, XCircle, Clock, Eye, ChevronDown, ChevronUp
} from 'lucide-react';
import api, { formatErrorMessage } from '../utils/api';
import { toast } from 'sonner';

const Oportunidades = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCrear, setShowCrear] = useState(false);
  const [showDetalle, setShowDetalle] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [expandedBitacora, setExpandedBitacora] = useState({});
  
  // Form state
  const [clientName, setClientName] = useState('');
  const [detail, setDetail] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Gestión state
  const [newGestion, setNewGestion] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCurrentUser();
    fetchOpportunities();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error al cargar usuario');
    }
  };

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      const response = await api.get('/opportunities');
      setOpportunities(response.data);
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    const uploadedFiles = [];
    
    for (const file of selectedFiles) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} excede el límite de 10MB`);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity_type', 'opportunity');
        formData.append('entity_id', 'pending');
        
        const response = await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedFiles.push({
          id: response.data.id,
          name: response.data.filename,
          url: response.data.url,
          size: response.data.size
        });
      } catch (error) {
        toast.error(`Error al subir ${file.name}`);
      }
    }
    
    setFiles([...files, ...uploadedFiles]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (fileId) => {
    setFiles(files.filter(f => f.id !== fileId));
  };

  const handleSubmit = async () => {
    if (!clientName.trim() || !detail.trim()) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('client_name', clientName);
      formData.append('detail', detail);
      formData.append('attachments', JSON.stringify(files.map(f => f.id)));
      
      await api.post('/opportunities', formData);
      
      toast.success('Oportunidad registrada exitosamente');
      setShowCrear(false);
      setClientName('');
      setDetail('');
      setFiles([]);
      fetchOpportunities();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDetalle = (opp) => {
    setSelectedOpp(opp);
    setNewStatus(opp.status);
    setNewGestion('');
    setShowDetalle(true);
  };

  const handleChangeStatus = async () => {
    if (!selectedOpp || !newStatus) return;
    
    try {
      await api.put(`/opportunities/${selectedOpp.id}/status?new_status=${encodeURIComponent(newStatus)}`);
      toast.success(`Estado actualizado a ${newStatus}`);
      fetchOpportunities();
      
      // Actualizar el objeto seleccionado
      setSelectedOpp({ ...selectedOpp, status: newStatus });
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleAddGestion = async () => {
    if (!selectedOpp || !newGestion.trim()) {
      toast.error('Ingrese una descripción');
      return;
    }
    
    try {
      await api.post(`/opportunities/${selectedOpp.id}/gestion?descripcion=${encodeURIComponent(newGestion)}`);
      toast.success('Gestión registrada');
      setNewGestion('');
      
      // Refrescar
      const response = await api.get(`/opportunities/${selectedOpp.id}`);
      setSelectedOpp(response.data);
      fetchOpportunities();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const toggleBitacora = (oppId) => {
    setExpandedBitacora(prev => ({ ...prev, [oppId]: !prev[oppId] }));
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Nueva': 'bg-blue-100 text-blue-700 border-blue-200',
      'Gestionando': 'bg-amber-100 text-amber-700 border-amber-200',
      'Lograda': 'bg-green-100 text-green-700 border-green-200',
      'No Lograda': 'bg-red-100 text-red-700 border-red-200'
    };
    
    const icons = {
      'Nueva': <Clock className="w-3 h-3" />,
      'Gestionando': <MessageSquare className="w-3 h-3" />,
      'Lograda': <CheckCircle className="w-3 h-3" />,
      'No Lograda': <XCircle className="w-3 h-3" />
    };
    
    return (
      <Badge className={`${styles[status] || 'bg-slate-100 text-slate-700'} flex items-center gap-1`}>
        {icons[status]}
        {status}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CL', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-10 h-10 text-primary" strokeWidth={1.5} />
              <div>
                <h1 className="text-4xl font-bold text-slate-900" data-testid="oportunidades-title">
                  Oportunidades de Venta
                </h1>
                <p className="text-lg text-slate-600">
                  Registra y gestiona potenciales negocios
                </p>
              </div>
            </div>
            
            <Button 
              onClick={() => setShowCrear(true)}
              className="bg-primary hover:bg-primary/90 flex items-center gap-2"
              data-testid="nueva-oportunidad-btn"
            >
              <Plus className="w-4 h-4" />
              Nueva Oportunidad
            </Button>
          </div>

          {/* Lista de Oportunidades */}
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : opportunities.length === 0 ? (
            <Card className="p-12 text-center border-slate-200">
              <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No hay oportunidades registradas</h3>
              <p className="text-slate-500 mb-4">Comienza registrando una nueva oportunidad de negocio</p>
              <Button onClick={() => setShowCrear(true)} className="bg-primary">
                <Plus className="w-4 h-4 mr-2" />
                Registrar Oportunidad
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {opportunities.map((opp) => (
                <Card 
                  key={opp.id} 
                  className="p-6 border-slate-200 hover:shadow-md transition-shadow"
                  data-testid={`oportunidad-card-${opp.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Building2 className="w-5 h-5 text-primary" />
                        <h3 className="text-lg font-semibold text-slate-900">{opp.client_name}</h3>
                        {getStatusBadge(opp.status)}
                      </div>
                      
                      <p className="text-slate-600 mb-3 line-clamp-2">{opp.detail}</p>
                      
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {opp.created_by}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(opp.created_at)}
                        </span>
                        {opp.attachments?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Paperclip className="w-4 h-4" />
                            {opp.attachments.length} archivo(s)
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {opp.puede_gestionar && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenDetalle(opp)}
                          className="flex items-center gap-1"
                          data-testid={`gestionar-btn-${opp.id}`}
                        >
                          <MessageSquare className="w-4 h-4" />
                          Gestionar
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleBitacora(opp.id)}
                        className="flex items-center gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        {expandedBitacora[opp.id] ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Bitácora expandible - solo para gestores */}
                  <AnimatePresence>
                    {expandedBitacora[opp.id] && opp.bitacora && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="mt-4 pt-4 border-t border-slate-200"
                      >
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">Bitácora de Gestiones</h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {opp.bitacora.map((entry, idx) => (
                            <div key={idx} className="flex items-start gap-3 text-sm p-2 bg-slate-50 rounded">
                              <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                              <div className="flex-1">
                                <p className="text-slate-700">{entry.descripcion}</p>
                                <p className="text-xs text-slate-500">
                                  {entry.usuario} • {formatDate(entry.fecha)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Modal: Nueva Oportunidad */}
      <Dialog open={showCrear} onOpenChange={setShowCrear}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Nueva Oportunidad de Venta
            </DialogTitle>
            <DialogDescription>
              Registra un potencial negocio para seguimiento
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="clientName">Nombre del Cliente *</Label>
              <Input
                id="clientName"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Empresa o persona de contacto"
                data-testid="input-client-name"
              />
            </div>
            
            <div>
              <Label htmlFor="detail">Detalle de la Oportunidad *</Label>
              <Textarea
                id="detail"
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                placeholder="Describe el potencial negocio, necesidades del cliente, servicios requeridos..."
                rows={4}
                data-testid="input-detail"
              />
            </div>
            
            <div>
              <Label>Archivos Adjuntos</Label>
              <div className="mt-2 space-y-2">
                {files.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="text-sm text-slate-700">{file.name}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(file.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                />
                
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full"
                >
                  {uploading ? (
                    <span className="animate-spin mr-2">⏳</span>
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploading ? 'Subiendo...' : 'Adjuntar Archivos'}
                </Button>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCrear(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !clientName.trim() || !detail.trim()}
                className="bg-primary hover:bg-primary/90"
                data-testid="submit-oportunidad-btn"
              >
                {submitting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Gestionar Oportunidad */}
      <Dialog open={showDetalle} onOpenChange={setShowDetalle}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Gestionar Oportunidad
            </DialogTitle>
            <DialogDescription>
              {selectedOpp?.client_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOpp && (
            <div className="space-y-6 mt-4">
              {/* Info de la oportunidad */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-slate-900">{selectedOpp.client_name}</h4>
                  {getStatusBadge(selectedOpp.status)}
                </div>
                <p className="text-sm text-slate-600">{selectedOpp.detail}</p>
                <p className="text-xs text-slate-500 mt-2">
                  Registrado por {selectedOpp.created_by} el {formatDate(selectedOpp.created_at)}
                </p>
              </div>
              
              {/* Cambiar estado */}
              <div className="flex items-center gap-4">
                <Label className="whitespace-nowrap">Cambiar Estado:</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="w-48" data-testid="select-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Nueva">Nueva</SelectItem>
                    <SelectItem value="Gestionando">Gestionando</SelectItem>
                    <SelectItem value="Lograda">Lograda</SelectItem>
                    <SelectItem value="No Lograda">No Lograda</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  onClick={handleChangeStatus}
                  disabled={newStatus === selectedOpp.status}
                  size="sm"
                >
                  Actualizar
                </Button>
              </div>
              
              {/* Bitácora */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-3">Bitácora de Gestiones</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto mb-4 p-3 bg-slate-50 rounded-lg">
                  {selectedOpp.bitacora?.length > 0 ? (
                    selectedOpp.bitacora.map((entry, idx) => (
                      <div key={idx} className="flex items-start gap-3 text-sm p-2 bg-white rounded border border-slate-100">
                        <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                        <div className="flex-1">
                          <p className="text-slate-700">{entry.descripcion}</p>
                          <p className="text-xs text-slate-500">
                            {entry.usuario} • {formatDate(entry.fecha)}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">Sin gestiones registradas</p>
                  )}
                </div>
                
                {/* Agregar gestión */}
                <div className="flex gap-2">
                  <Input
                    value={newGestion}
                    onChange={(e) => setNewGestion(e.target.value)}
                    placeholder="Agregar nota o gestión..."
                    data-testid="input-nueva-gestion"
                  />
                  <Button onClick={handleAddGestion} disabled={!newGestion.trim()}>
                    <Plus className="w-4 h-4 mr-1" />
                    Agregar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Oportunidades;
